import React, { useState, useRef, useContext, useCallback } from "react";
import { Container, Grid, Paper, Tabs, Tab, Typography, Box, Button } from "@mui/material";
import UserPageGeneral from "./userPageGeneral";
import UserPageChangepassword from "./userPageChangepassword";
import { useIsMobile } from "../../../util/utils";
import { UserContext } from "../../../components/auth/auth-context";
import { setSelectedBusiness } from "../../../redux/actions/businessActions";
import { useSelectBusinesses } from "../../../redux/store";
import useLoadReduxStoreData from "../../../redux/reducers/loadReduxStoreData";
import { BusinessQueryNames } from "../../../graphql/queries";
import { UPDATE_PROFILE } from "../../../graphql/mutations";
import { useMutation } from "@apollo/client";
import { useDispatch } from "react-redux";
import DefaultPageDialog, { pageOptions } from "../defaultPageDialog";

const headerTitles = ["General Profile", "Change Your Password"];

const UserPage = () => {
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);
  const isMobile = useIsMobile();
  const { providerUser: token } = useContext(UserContext);

  // Load businesses
  useLoadReduxStoreData(BusinessQueryNames.GET_MY_BUSINESSES);
  const businesses = useSelectBusinesses();

  // Profile mutation for general form
  const [update] = useMutation(UPDATE_PROFILE);

  // Existing profile state
  const [username, setUsername] = useState(token.username);
  const [email, setEmail] = useState(token.email);
  const [desc, setDesc] = useState(token.desc);
  const [defaultBusinessId, setDefaultBusinessId] = useState(token.defaultBusinessId);
  const [selectedTimeZoneId, setSelectedTimeZoneId] = useState(token.timeZone?.id);
  const [generalUpdated, setGeneralUpdated] = useState(false);
  const [errors, setErrors] = useState("");

  // Track defaultPageId and derive label
  const [defaultPageId, setDefaultPageId] = useState(
    token.defaultPageId != null ? token.defaultPageId : 1
  );
  const defaultPageLabel =
    pageOptions.find((opt) => opt.id === defaultPageId)?.label || "";

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const openDialog = () => setDialogOpen(true);
  const closeDialog = () => setDialogOpen(false);

  // Parent callback when dialog saves
  const handleDialogSaved = (pageId) => {
    setDefaultPageId(pageId);
  };

  // Default business change handler
  const handleSelectDefaultBusinessChange = async (e) => {
    const selectedBusinessId = e.target.value;
    const selectedBusiness = businesses.find(
      (business) => business.businessBasic.id === selectedBusinessId
    );
    if (selectedBusiness) {
      try {
        setGeneralUpdated(true);
        setDefaultBusinessId(selectedBusiness.businessBasic.id);
        // Simulate backend update
        handleSubmit();
        dispatch(setSelectedBusiness(selectedBusinessId));
      } catch (error) {
        console.error("Failed to update default business:", error);
      }
    }
  };

  // General form submit (other fields)
  const handleSubmit = () => {
    setGeneralUpdated(false);
    update({
      variables: {
        userId: Number(token.userId),
        email,
        username,
        hours24: token.hours24,
        timeZoneId: Number(selectedTimeZoneId),
        desc,
        planId: token.planId,
        calendar: token.calendar,
        defaultBusinessId,
        defaultPageId,
      },
    })
      .then((res) => {
        setErrors("");
        localStorage.setItem("token", res.data.updateProfile.token);
      })
      .catch((err) => console.error(err));
  };

  const handleTimezoneSelect = useCallback(
    (tz) => {
      if (tz.timezone !== token.timeZone?.timezone) {
        setSelectedTimeZoneId(tz.id);
        setGeneralUpdated(true);
      }
    },
    [token.timeZone]
  );

  const notificationRef = useRef();
  const handleTabChange = (_, newValue) => setTabValue(newValue);

  return (
    <>
      <Box>
        <Typography
          variant="h5"
          sx={{ fontSize: { xs: "1.5rem", sm: "2rem" }, textAlign: "center" }}
        >
          {headerTitles[tabValue]}
        </Typography>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 8 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={isMobile ? 12 : 3}>
            <Paper>
              <Tabs
                orientation={isMobile ? "horizontal" : "vertical"}
                value={tabValue}
                onChange={handleTabChange}
              >
                <Tab label="General" />
                <Tab label="Change Password" />
                <Tab label=" Set Default Start Page" />
              </Tabs>

            </Paper>

          </Grid>

          <Grid item xs={12} sm={isMobile ? 12 : 9}>
            <Paper>
              {tabValue === 0 && (
                <Box p={2}>

                  <UserPageGeneral
                    defaultBusinessId={defaultBusinessId}
                    defaultBusinessLoading={false}
                    defaultBusinessError={null}
                    businesses={businesses}
                    handleSelectDefaultBusinessChange={
                      handleSelectDefaultBusinessChange
                    }
                    username={username}
                    setUsername={setUsername}
                    setUpdated={setGeneralUpdated}
                    email={email}
                    selectedTimeZoneId={selectedTimeZoneId}
                    token={token}
                    handleTimezoneSelect={handleTimezoneSelect}
                    handleSubmit={handleSubmit}
                    updated={generalUpdated}
                    notificationRef={notificationRef}
                    desc={desc}
                    setDesc={setDesc}
                    errors={errors}
                  />
                </Box>
              )}

              {tabValue === 1 && (
                <UserPageChangepassword error={false} setError={() => { }} />
              )}

              {tabValue === 2 &&
                <>
                  {defaultBusinessId && <>
                    <Button
                      onClick={openDialog}
                      sx={{ mb: 1, width: '100%' }}
                    >
                      Set Default Start Page
                    </Button>

                    <Typography
                      variant="body2"
                      sx={{ textAlign: 'center', color: 'text.secondary' }}
                    >
                      This page will open automatically every time you log in — until you change it.
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ mt: 0.5, textAlign: 'center', fontWeight: 500 }}
                    >
                      📌 Selected: <strong>{defaultPageLabel}</strong>
                    </Typography>
                  </>
                  }
                </>
              }


            </Paper>
          </Grid>
        </Grid>
      </Container>
      <DefaultPageDialog
        open={dialogOpen}
        value={defaultPageId}
        onClose={closeDialog}
        onSaved={handleDialogSaved}
      />
    </>
  );
};

export default UserPage;
